import React, { useState, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { date, buildExternalLink, openExternalLink, EXTERNAL_LINK_BASES, storage } from 'utils'

import type { Company, CompanyListRow } from 'types'

import { useCanAccess, useDisclosure, useDebouncedValue, useReferrerState } from 'hooks'
import { useCompaniesQuery, useCompanyMutationsQuery, useVeraSpaceMutationsQuery } from 'hooks/api'
import { useSourceChannels } from 'hooks/api/useGtmSales'
import { sourceChannelLabel } from 'constants/sourceChannels'
import { useVeraSpaceStore } from 'stores'
import { PlusIcon, StoreIcon, DoorOpenIcon, InfoIcon, OctagonPauseIcon, CircleXIcon, ArchiveIcon, ArchiveRestoreIcon, UserPlusIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { Stack, useTheme, IconButton, Tooltip, Typography } from '@mui/material'

import { Button } from '@repo/ui'
import {
  CreateCompanyModal,
  Header,
  Table,
  EmptyState,
  ActionDropdown,
  CompanyCell,
  DeleteCompanyModal,
  InviteInternalUserModal,
  SuspendAccessModal,
  CancelSubscriptionInternalModal,
  ArchiveCompanyModal,
  UserGroupCell,
  StatusChip,
  headerWithTerm,
} from 'components'
import { BRAND_ICONS } from '../CompanyPage/brand-icons'
import { useWebsiteDisplayColumns } from './websiteDisplayColumns'
import { payingNowColumn } from '../GtmSalesPage/AutoWonMarks'
import { companyActivationColumns } from './companyActivationColumns'
import {
  COMPANY_STATUSES,
  COMPANY_COMP_TONE,
  COMPANY_GRANTED_ACTIVATIONS_TONE,
  PLAN_STATUSES,
  COMPANY_SEGMENT_STATUSES,
} from 'constants/statuses'
import { DEFAULT_COMPANY_SEGMENTS, isCompanySegment, type CompanySegment } from '@repo/types'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'

/**
 * VRG-718 — the Status filter offers only the states that live INSIDE the loaded segments.
 * Archived is a segment now (the Type facet), and a demo is a type, so offering either here
 * would be a client filter over rows the server never sent.
 */
const COMPANY_STATUS_FILTER = { active: COMPANY_STATUSES.active, suspended: COMPANY_STATUSES.suspended }

/** VRG-718 — the drawer's starting state: the Type facet on Signed up. */
const DEFAULT_TABLE_FILTERS = [{ field: 'segment', operator: 'isAnyOf', value: [...DEFAULT_COMPANY_SEGMENTS] }]

// The `/companies/admin` row (VRG-706: one shared type, `CompanyListRow`, so this page and
// `useCompaniesQuery` can no longer describe the same payload two ways).
type CompanyRow = CompanyListRow & { vera_managers?: any[] }

// VERA-873: external-tool deep-link column (Apollo / Stripe / Shopify).
// Renders a branded icon button that opens the company's record in that tool.
// Falls back to a centered em dash when the id is missing — expected to be rare,
// since every company should carry these ids.
const externalLinkColumn = (
  field: 'apollo' | 'stripe' | 'shopify',
  headerName: string,
  base: string,
  getId: (company: Company) => string | number | null | undefined,
) => ({
  field,
  headerName,
  type: 'custom',
  width: 100,
  align: 'center',
  headerAlign: 'center',
  renderCell: ({ row }: { row: CompanyRow }) => {
    const url = buildExternalLink(base, getId(row.company))
    return (
      <Stack width="100%" height="100%" alignItems="center" justifyContent="center">
        {url ? (
          <Tooltip title={`Open in ${headerName}`}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); openExternalLink(url) }}
              sx={{ color: 'text.secondary', borderRadius: 1.5, '&:hover': { color: 'text.primary' } }}
            >
              {BRAND_ICONS[field]}
            </IconButton>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled"></Typography>
        )}
      </Stack>
    )
  },
})

export const CompaniesPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  // Where "Return to Internal" comes back to: the page they were ACTUALLY on,
  // path + query so filters and pagination survive (§42). Detail pages included.
  const location = useLocation()
  const { open: notification } = useSnackbar()
  // Referrer state handed to every drill-in so the destination Header can offer
  // "← Back to Companies".
  const referrer = useReferrerState()

  // Pagination + sort state
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | undefined>(undefined)

  // Search state (raw + debounced)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedQ] = useDebouncedValue(searchInput, 300)

  // VRG-718 — the server-side filters. The list is server-paginated, so a client filter would
  // narrow only the loaded page and the header total would lie; these go to the API instead.
  // `segments` defaults to ALL SIX — every company (2026-11-02; see DEFAULT_COMPANY_SEGMENTS for
  // why this reversed VRG-718's Signed-up-only default). It still replaces the old always-on
  // `includeDemo` + `showArchived` pair: the Type chips partition the table, so narrowing to
  // Signed up is one chip away. The seven company pickers sharing this endpoint send no
  // `segments` and keep their own exclusive default.
  const [segments, setSegments] = useState<CompanySegment[]>([...DEFAULT_COMPANY_SEGMENTS])
  const [activated, setActivated] = useState<boolean | undefined>(undefined)
  const [activationsRange, setActivationsRange] = useState<{ min?: number; max?: number }>({})

  const filters = {
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    sortField,
    sortDir,
    q: debouncedQ || undefined,
    segments,
    activated,
    activationsMin: activationsRange.min,
    activationsMax: activationsRange.max,
  }

  const [selectedCompany, setSelectedCompany] = useState<CompanyRow | null>(null)

  const { data, refetch, isLoading } = useCompaniesQuery(filters)
  // VRG-675 — explicit company source channels, one small map for the whole list.
  const { data: sourceChannels } = useSourceChannels()
  const companies = data?.data || []
  const total = data?.total || 0

  const [openArchiveModal, setOpenArchiveModal] = useState(false)

  const [openDeleteModal, { open: handleOpenDeleteModal, close: handleCloseDeleteModal }] = useDisclosure(false, {
    onClose: () => {
      setSelectedCompany(null)
    },
  })
  const [openSuspendAccessModal, setOpenSuspendAccessModal] = useState(false)
  const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] = useState(false)
  const [openCreateCompanyModal, setOpenCreateCompanyModal] = useState(false)
  const [openInviteInternalUserModal, { open: handleOpenInviteInternalUserModal, close: handleCloseInviteInternalUserModal }] = useDisclosure(false)

  const { isHasAccess: isCanViewCompanyUsers } = useCanAccess({ action: 'users_view' })
  const { isHasAccess: isCanInviteUser } = useCanAccess({ action: 'users_invite' })
  const { isHasAccess: isCanManageCompany } = useCanAccess({ action: 'manage_company' })
  const { isHasAccess: isCanDeleteCompany } = useCanAccess({ action: 'delete_company' })
  const { isHasAccess: isCanViewPlatformAdmin } = useCanAccess({ role: 'platform_admin' })
  const { isHasAccess: isCanViewPlatformManager } = useCanAccess({ role: 'platform_manager' })
  const { isHasAccess: isCanSuspendCompany } = useCanAccess({ role: 'platform_admin' })
  const { isHasAccess: isCanViewCompanyAdmin } = useCanAccess({ role: 'company_admin' })
  // VERA-876: gating for "Go to space" — requires internal_company_enter_space permission
  // We need the raw profile permission here (not the space-scoped version) because this
  // check determines whether the staffer can ENTER a space, not what they can do inside one.
  // useCanAccess reads from profile.role.permissions in normal mode (no active space), so
  // this correctly reflects the staffer's actual permission.
  const { isHasAccess: isCanEnterSpace } = useCanAccess({ action: 'internal_company_enter_space' })

  const isCanViewCompanyManagers = isCanViewPlatformAdmin || isCanViewPlatformManager || isCanViewCompanyAdmin

  // Website display switches + publicity opt-out (API migration 0142). Writes are
  // platform-admin only, same gate as suspend/archive: a switch puts a third party's
  // trademark on vera.inc. See websiteDisplayColumns.tsx for the rule.
  const { columns: websiteDisplayColumns, modal: websiteDisplayModal } = useWebsiteDisplayColumns({
    canEdit: isCanSuspendCompany,
  })

  // Unarchive mutation (direct — no modal needed)
  const { unarchive: unarchiveCompany } = useCompanyMutationsQuery({
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  // VERA-876: vera space entry — replaces the old startImpersonation / window.location.href pattern
  const enterSpace = useVeraSpaceStore((s) => s.enter)
  const { enterAsync: fetchVeraGrant } = useVeraSpaceMutationsQuery({
    onEnterError: () => {
      notification({ type: 'error', title: 'Failed to enter company space.' })
    },
  })

  const handleViewDetails = (id: string) => {
    navigate(ROUTES.INTERNAL_COMPANY(id), { state: referrer })
  }

  const handleEditCompany = (id: string) => {
    navigate(ROUTES.INTERNAL_EDIT_COMPANY(id), { state: referrer })
  }

  const handleDelete = (row: CompanyRow) => {
    handleOpenDeleteModal()
    setSelectedCompany(row)
  }

  const handleInviteUser = (row: CompanyRow) => {
    setSelectedCompany(row)
    handleOpenInviteInternalUserModal()
  }

  const handleSuspendAccess = (row: CompanyRow) => {
    setOpenSuspendAccessModal(true)
    setSelectedCompany(row)
  }

  const handleCloseSuspendAccessModal = () => {
    setOpenSuspendAccessModal(false)
  }

  const handleCancelSubscription = (row: CompanyRow) => {
    setOpenCancelSubscriptionModal(true)
    setSelectedCompany(row)
  }

  const handleCloseCancelSubscriptionModal = () => {
    setOpenCancelSubscriptionModal(false)
  }

  const handleCloseCreateCompanyModal = () => {
    setOpenCreateCompanyModal(false)
  }

  const handleArchive = (row: CompanyRow) => {
    setSelectedCompany(row)
    setOpenArchiveModal(true)
  }

  const handleCloseArchiveModal = () => {
    setOpenArchiveModal(false)
  }

  const handleUnarchive = (row: CompanyRow) => {
    unarchiveCompany({ companyId: row.company.id })
  }

  // VRG-718 — the drawer's applied set → the server params. Type (segment) and Activated are
  // multi-select chips; Activations is the shared range slider ('>=' / '<='). A cleared Type
  // means every segment (the hook sends all six), not back to the default.
  const handleFiltersChange = (applied: Array<{ field: string; operator?: string; value: unknown }>) => {
    const segmentItem = applied.find((f) => f.field === 'segment')
    const nextSegments = Array.isArray(segmentItem?.value) ? segmentItem.value.filter(isCompanySegment) : []
    setSegments(nextSegments)

    const activatedItem = applied.find((f) => f.field === 'first_activated_at')
    const picked: string[] = Array.isArray(activatedItem?.value) ? (activatedItem.value as string[]) : []
    // Both chips (or neither) is no condition.
    setActivated(picked.length === 1 ? picked[0] === 'activated' : undefined)

    const num = (op: string) => {
      const v = applied.find((f) => f.field === 'activations_count' && f.operator === op)?.value
      return v === null || v === undefined || v === '' ? undefined : Math.round(Number(v))
    }
    setActivationsRange({ min: num('>='), max: num('<=') })
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }

  const onClickViewCompanyUsers = (id: string) => {
    if (!isCanViewCompanyUsers) return
    navigate(ROUTES.INTERNAL_COMPANY(id), { state: referrer })
  }

  const handleGoIntoSpace = async (row: CompanyRow) => {
    try {
      const grant = await fetchVeraGrant({ companyId: row.company.id })
      enterSpace({
        companyId: row.company.id,
        companyName: grant.companyName,
        permissions: grant.permissions,
        // Where "Return to Internal" goes (VRG-369). This page — which is also the
        // banner's fallback, stated explicitly rather than relied on, so the
        // fallback covers sessions that forgot rather than being the behaviour
        // every caller silently depends on.
        returnTo: `${location.pathname}${location.search}`,
      })
      // Land on the brand's own Company Settings (not HOME — HOME redirects a
      // platform_admin back to the internal console, which is locked out in-space).
      // Company settings is backed by the view_company-gated /companies/admin/me
      // endpoint, and view_company is in the default view-only grant, so the surface
      // loads for a capped Vera staffer. X-Vera-Company is derived from the veraSpace
      // store in services/index.ts, so we don't depend on the fragile `selectedCompany`
      // key (the auth flow clears it for platform admins). Still set it for parity.
      storage.setItem('selectedCompany', row.company.id)
      navigate(ROUTES.SETTINGS_COMPANY_GENERAL)
    } catch (err) {
      console.error('Failed to enter company space', err)
    }
  }

  // §27 — in-context row actions gated on a permission/role stay VISIBLE and go
  // disabled+tooltip (never hidden); a capped staffer must be able to see the
  // kebab has actions in it and know exactly which permission is missing.
  // `visible:` is reserved for RECORD STATE (isArchived / subscription
  // presence) — the permission/role check always lands in `disabled` +
  // `tooltip`. Below this file, see CompanyPage.tsx / PackagesPage.tsx /
  // InternalTagsPage.tsx / SubscriptionsPage.tsx for the same pattern.
  const platformAdminTooltip = (verb: string) => (isCanSuspendCompany ? undefined : `Only a Vera admin can ${verb}.`)
  const getActionItems = useCallback((row) => {
    const isArchived = !!row.company.archived
    return [
      {
        label: 'Details',
        onClick: () => handleViewDetails(row.company.subdomain ?? row.company.id),
        visible: true,
        disabled: !isCanViewCompanyUsers,
        tooltip: isCanViewCompanyUsers ? undefined : "You need the 'View users' permission. Ask a Vera admin.",
        icon: <InfoIcon strokeWidth={1}/>
      },
      {
        label: 'Go to space',
        onClick: () => handleGoIntoSpace(row),
        visible: !isArchived,
        disabled: !isCanEnterSpace,
        tooltip: isCanEnterSpace ? undefined : "You need the 'Enter company workspace' permission. Ask a Vera admin.",
        // §21: the same action carries the same icon across surfaces. Entering a
        // company's space is `DoorOpenIcon`, matching the Demos console kebab.
        // `ArrowUpRightIcon` is this app's NAVIGATE glyph (external links, reference
        // cells), and entering a space is not navigation — it is assuming a scoped
        // identity inside another company, which is why it has its own door.
        icon: <DoorOpenIcon strokeWidth={1}/>
      },
      {
        label: 'Invite user',
        onClick: () => handleInviteUser(row),
        visible: !isArchived,
        disabled: !isCanInviteUser,
        tooltip: isCanInviteUser ? undefined : "You need the 'Invite users' permission. Ask a Vera admin.",
        icon: <UserPlusIcon strokeWidth={1} />,
      },
      {
        label: 'Cancel subscription',
        onClick: () => handleCancelSubscription(row),
        visible: !!row.company.subscription?.stripe_subscription_id,
        disabled: !isCanSuspendCompany,
        tooltip: platformAdminTooltip('cancel a subscription'),
        destructive: true,
        icon: <CircleXIcon strokeWidth={1} color={theme.palette.error.main} />
      },
      {
        label: 'Suspend access',
        onClick: () => handleSuspendAccess(row),
        visible: !isArchived,
        disabled: !isCanSuspendCompany,
        tooltip: platformAdminTooltip('suspend access'),
        divider: true,
        destructive: true,
        icon: <OctagonPauseIcon strokeWidth={1} color={theme.palette.error.main} />
      },
      ...(isArchived ? [{
        label: 'Unarchive',
        onClick: () => handleUnarchive(row),
        visible: true,
        disabled: !isCanSuspendCompany,
        tooltip: platformAdminTooltip('unarchive a company'),
        divider: true,
        icon: <ArchiveRestoreIcon strokeWidth={1} />
      }] : [{
        label: 'Archive',
        onClick: () => handleArchive(row),
        visible: true,
        disabled: !isCanSuspendCompany,
        tooltip: platformAdminTooltip('archive a company'),
        divider: !isCanSuspendCompany,
        icon: <ArchiveIcon strokeWidth={1} />
      }]),
    ]
  }, [isCanViewCompanyUsers, isCanEnterSpace, isCanInviteUser, isCanSuspendCompany, theme.palette.error.main, referrer])

  const getCompanyStatus = (company: Company): string => {
    // VRG-718: a demo no longer reads "Demo" HERE — the Type column says what a company is
    // (Demo / Example / Test / Internal), so Status is back to its own axis: lifecycle state.
    if (company.archived) return 'archived'
    if (company.suspended) return 'suspended'
    return 'active'
  }

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        disableReorder: true,
        pinned: 'left',
        // No bulk-select toggle on this table, so the search box sits ~70px
        // further left than on tables that have one — 298px (not the 368px
        // default) lines this column's right border up with it.
        width: 298,
        renderCell: ({ row }) => (<CompanyCell company={row.company}/>)
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'statusChip',
        options: statusFilterOptions(COMPANY_STATUS_FILTER),
        // Companies have no top-level `status` field (it's derived from
        // archived/suspended), so the client-side filter needs a valueGetter to
        // match the selected multiSelect values — otherwise the selected row is
        // loaded server-side but hidden by the client filter.
        valueGetter: (value, row) => (row?.company ? getCompanyStatus(row.company) : row?.status),
        width: 130,
        // renderCell is called both for data rows (row.company is a Company) and
        // from MultiSelectFilter for option chips (row = { status: optionValue }).
        renderCell: ({ row }) => (
          <StatusChip
            statuses={COMPANY_STATUSES}
            status={row.company ? getCompanyStatus(row.company) : (row.status as string)}
            size="small"
          />
        ),
      },
      {
        // VRG-718 — the Type facet and row badge. The FACET is the segment (Signed up / Demo /
        // Example / Test / Internal / Archived: one per company, filtered on the server); the
        // BADGE is what the company is, so a signed-up row wears none and an archived demo
        // still reads Demo. Both come from the API's one SQL expression (company-list-segments).
        field: 'segment',
        headerName: 'Type',
        renderHeader: headerWithTerm('Type', 'company-segment'),
        type: 'statusChip',
        options: statusFilterOptions(COMPANY_SEGMENT_STATUSES),
        // Server-filtered; the grid's own filter then matches on the same value, a no-op.
        valueGetter: (_value: unknown, row: CompanyRow) => row?.segment ?? null,
        // Not server-sortable (buildSortOrder), so no sort control that would do nothing.
        sortable: false,
        width: 128,
        renderCell: ({ row }: { row: CompanyRow }) =>
          row.company_type ? (
            <StatusChip statuses={COMPANY_SEGMENT_STATUSES} status={row.company_type} size="small" />
          ) : null,
      },
      {
        // VRG-372: the company's current plan, flagged "Retired" when it's no
        // longer sold (stripe_subscription_products.active = false) — driven by
        // the plan's `active` flag, never a hardcoded plan-name list (AC4). No
        // false positives: an active plan renders with no chip at all (AC2).
        field: 'plan',
        headerName: 'Plan',
        type: 'custom',
        width: 220,
        // Not wired into buildSortOrder server-side (mirrors the Comped/Granted
        // columns' non-sortable rationale above) — a sort click here would no-op.
        sortable: false,
        renderCell: ({ row }: { row: CompanyRow }) => {
          const plan = row.plan
          if (!plan) {
            return (
              <Stack width="100%" height="100%" alignItems="center" justifyContent="flex-start">
                <Typography variant="body2" color="text.disabled">—</Typography>
              </Stack>
            )
          }
          return (
            <Stack direction="row" spacing={2} alignItems="center" width="100%" height="100%">
              <Typography
                variant="body2"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {plan.name ?? '—'}
              </Typography>
              {plan.active === false && (
                <StatusChip statuses={PLAN_STATUSES} status="retired" size="small" />
              )}
            </Stack>
          )
        },
      },
      // VRG-727 — Paying now / Not paying / Never paid: the same rule as GTM → Sales (a live
      // paid invoice above $0 in the last 60 days). Filters the loaded page, like Status; empty
      // for roles that do not get the field.
      { ...payingNowColumn<CompanyRow>((row) => row.paying_now), sortable: false },
      {
        field: 'country',
        headerName: 'Headquarters',
        type: 'string',
        // §49: 150, up from 120, where the header rendered as "Headq…". Sized for
        // the HEADER, not the values — the cell holds a 2-letter country code, which
        // is what made 120 look sufficient. `Certificates` is the same 12 characters
        // and sits at 150 without clipping, so this is matched against a working
        // column rather than estimated from character count.
        width: 150,
        renderCell: ({ row }) => row.company.country,
      },
      {
        // VRG-675 — the channel SET on the company (Company page → Set source channel).
        // A company with none counts in GTM → Sales under its signup's channel; this column
        // shows only what was recorded on the company itself. Filters the loaded page,
        // like Status; not server-sortable, so sorting is off.
        field: 'source_channel',
        headerName: 'Source channel',
        type: 'multiSelect',
        width: 180,
        sortable: false,
        valueGetter: (_: unknown, row: CompanyRow) => sourceChannelLabel(sourceChannels?.companies[row.company.id]),
        // The filter drawer calls this with `{ filter: true, value: <label> }` and no company.
        renderCell: ({ row, value, filter }: { row: CompanyRow; value?: string; filter?: boolean }) => {
          if (filter) return value
          const c = sourceChannels?.companies[row.company.id]
          return (
            <Typography variant="body2" color={c ? 'text.primary' : 'text.disabled'} noWrap>
              {c ? sourceChannelLabel(c) : '—'}
            </Typography>
          )
        },
      },
      {
        field: 'customers_count',
        headerName: 'Owners',
        type: 'number',
        width: 150,
        renderCell: ({row}) => row.customers_count || 0,
      },
      {
        field: 'company_managers_count',
        headerName: 'Users',
        width: 150,
        type: 'number',
        renderCell: ({row}) => row.company_managers_count || 0
      },
      {
        field: 'certificates_count',
        headerName: 'Certificates',
        type: 'number',
        width: 150,
        renderCell: ({row}) => row.certificates_count || 0,
      },
      // VRG-718 — Activated (milestone date; yes/no facet) and Activations (billable count, 30d
      // beside it; range facet). Shared with Sales → Companies. Both facets and both sorts are
      // SERVER-side here (handleFiltersChange / buildSortOrder), because this list is paginated.
      ...companyActivationColumns<CompanyRow>((row) => row),
      {
        field: 'active_comp_duration_months',
        headerName: 'Comped',
        // The column shows a DURATION ("3 mo") and the word above it is the only place the
        // reader learns what was comped. It is the plan fee, not the account: a comped
        // company still pays for every activation and every tag. That is the fact this
        // tooltip exists to carry, and the one most often assumed the other way.
        renderHeader: headerWithTerm('Comped', 'comp'),
        type: 'custom',
        // 110 -> 128 for the +18px glyph (§49).
        width: 128,
        align: 'center',
        headerAlign: 'center',
        // VRG-247: the backend's buildSortOrder only supports name/country/created_at —
        // wiring a sort click here would silently no-op server-side, so this stays
        // non-sortable rather than shipping a control that does nothing.
        sortable: false,
        renderCell: ({ row }: { row: CompanyRow }) => {
          const months = row.active_comp_duration_months
          if (!months) {
            return (
              <Stack width="100%" height="100%" alignItems="center" justifyContent="center">
                <Typography variant="body2" color="text.disabled">—</Typography>
              </Stack>
            )
          }
          // Duration is NOT a bounded 3-or-6 enum — a comp can be any 1-24 months
          // (the "Custom" option on CompSubscriptionModal), so the single-entry
          // statuses map is built per-cell keyed by the actual duration. Keying off
          // a fixed {"3","6"} map would render an unstyled/blank pill for a 9-month
          // comp. Same technique as the Granted column below.
          return (
            <Stack width="100%" height="100%" alignItems="center" justifyContent="center">
              <StatusChip
                size="small"
                statuses={{
                  [String(months)]: {label: `${months} mo`, ...COMPANY_COMP_TONE},
                }}
                status={String(months)}
              />
            </Stack>
          )
        },
      },
      {
        field: 'granted_activations_total',
        headerName: 'Granted',
        type: 'custom',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        // VRG-247: same non-sortable rationale as the Comped column above.
        sortable: false,
        renderCell: ({ row }: { row: CompanyRow }) => {
          const total = row.granted_activations_total
          if (!total) {
            return (
              <Stack width="100%" height="100%" alignItems="center" justifyContent="center">
                <Typography variant="body2" color="text.disabled">—</Typography>
              </Stack>
            )
          }
          // Not a bounded enum (unlike COMPANY_COMP_STATUSES) — the total is an
          // unbounded count, so the single-entry statuses map is built per-cell,
          // keyed by the total itself, reusing the shared StatusChip pill.
          return (
            <Stack width="100%" height="100%" alignItems="center" justifyContent="center">
              <StatusChip
                size="small"
                statuses={{ [String(total)]: { label: String(total), ...COMPANY_GRANTED_ACTIVATIONS_TONE } }}
                status={String(total)}
              />
            </Stack>
          )
        },
      },
      ...websiteDisplayColumns,
      ...(isCanViewPlatformAdmin || isCanViewPlatformManager ? [{
        field: 'vera_managers',
        headerName: 'Vera Managers',
        width: 200,
        type: 'custom',
        renderCell: ({row}) => {
          if (row.vera_managers && row.vera_managers.length > 0) {
            return <UserGroupCell users={row.vera_managers} route={(id) => ROUTES.INTERNAL_USER(id)} />
          }
          return row.vera_managers_count || 0
        }
      }] : []),
      {
        field: 'created_at',
        headerName: 'Join date',
        width: 200,
        type: 'dateTime',
        valueGetter: (value, row) => row.company.created_at && new Date(row.company.created_at),
        renderCell: ({ value }) => (value ? date(value) : '')
      },
      externalLinkColumn('apollo', 'Apollo', EXTERNAL_LINK_BASES.apollo, (c) => c.apollo_id),
      externalLinkColumn('stripe', 'Stripe', EXTERNAL_LINK_BASES.stripeCustomer, (c) => c.stripe_customer_id),
      externalLinkColumn('shopify', 'Shopify', EXTERNAL_LINK_BASES.shopifyCustomer, (c) => c.shopify_customer_id),
      {
        field: 'actions',
        headerName: false,
        disableReorder: true,
        // §27 — the kebab always renders now; each item carries its own
        // disabled+tooltip state (was previously hidden entirely behind
        // `isShowActionButton`, which left a capped staffer with no kebab at
        // all).
        renderCell: ({ row }) => (<ActionDropdown items={getActionItems(row)} />),
        align: 'center',
        width: 56,
        pinned: 'right',
      },
    ],
    [isCanViewCompanyManagers, isCanViewPlatformAdmin, isCanViewPlatformManager, getActionItems, sourceChannels, websiteDisplayColumns]
  )

  return (
    <>
     <Header
        title="Companies"
        total={total}
        rightComponents={
          // §27 — a header action stays visible+disabled+tooltip without the
          // permission (never hidden). A disabled MUI Button swallows pointer
          // events, so the Tooltip needs a non-disabled span wrapper to have
          // something to attach its hover listener to.
          <Tooltip title={isCanManageCompany ? '' : "You need the 'Manage company' permission. Ask a Vera admin."}>
            <span style={{ display: 'inline-flex' }}>
              <Button
                disableElevation
                disabled={!isCanManageCompany}
                onClick={() => setOpenCreateCompanyModal(true)}
                startIcon={<PlusIcon strokeWidth={1} />}
              >
                Create Company
              </Button>
            </span>
          </Tooltip>
        }
      />
      <Table
        fillBottom
        loading={isLoading}
        onSearchChange={(value) => {
          setSearchInput(value)
          setPaginationModel(prev => ({ ...prev, page: 0 }))
        }}
        rows={companies}
        columns={columns}
        getRowId={(row) => row.company?.id || row.id}
        paginationMode="server"
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        filters={DEFAULT_TABLE_FILTERS}
        onFiltersChange={handleFiltersChange}
        sortingMode="server"
        onSortModelChange={(model) => {
          const first = model[0]
          if (first) {
            setSortField(first.field)
            setSortDir(first.sort as 'asc' | 'desc')
          } else {
            setSortField(undefined)
            setSortDir(undefined)
          }
          setPaginationModel(prev => ({ ...prev, page: 0 }))
        }}
        emptyState={
          <EmptyState
            title="No companies created"
            text="Use the Create Company action above to add a new company"
            icon={<StoreIcon strokeWidth={1} size={32}/>}
          />
        } />
        <DeleteCompanyModal
          company={selectedCompany?.company || null}
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          refetch={refetch}
        />
        <InviteInternalUserModal
        companyId={selectedCompany?.company?.id}
        type="company"
        open={openInviteInternalUserModal}
        onClose={handleCloseInviteInternalUserModal}
        />
        <CreateCompanyModal
          open={openCreateCompanyModal}
          onClose={handleCloseCreateCompanyModal}
          refetch={refetch}
        />
        <SuspendAccessModal
          company={selectedCompany?.company || null}
          open={openSuspendAccessModal}
          onClose={handleCloseSuspendAccessModal}
          refetch={refetch}
        />
        <CancelSubscriptionInternalModal
          company={selectedCompany?.company || null}
          open={openCancelSubscriptionModal}
          onClose={handleCloseCancelSubscriptionModal}
          refetch={refetch}
        />
        <ArchiveCompanyModal
          company={selectedCompany?.company || null}
          open={openArchiveModal}
          onClose={handleCloseArchiveModal}
          refetch={refetch}
        />
        {websiteDisplayModal}
    </>
  )
}
